import React from 'react';
import { Box, TableCell, TableRow } from '@mui/material';
import { Repeat } from '@mui/icons-material';
import { NavLink } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { formatDateTime12Hour } from 'shared/utils/dateUtils';
import { getTodoPriorityDisplay, getTodoStatusDisplay, type TodoTableRow } from 'core/utils/todoScheduleEvent';
import EmployeeWidgetCard from './EmployeeWidgetCard';

interface EmployeeTodoWidgetProps {
	loading: boolean;
	items: TodoTableRow[];
}

const getUserFullName = (user: TodoTableRow['user']): string => {
	if (!user || typeof user !== 'object') return 'Unknown User';
	const record = user as Record<string, unknown>;
	const parts = [record.firstName, record.middleName, record.lastName].filter(
		(part) => typeof part === 'string' && part.trim() !== '',
	);
	return parts.join(' ') || 'Unknown User';
};

const getAssignmentDisplay = (todo: TodoTableRow): string => {
	if (todo.user) return getUserFullName(todo.user);
	if (todo.assignedGroup && typeof todo.assignedGroup === 'object') {
		const group = todo.assignedGroup as Record<string, unknown>;
		return (group.label as string) || 'Group Assignment';
	}
	if (todo.assigned) return todo.assigned;
	return 'Unassigned';
};

const getDescriptionDisplay = (todo: TodoTableRow): string => {
	let noteValue: string | undefined;
	if (typeof todo.note === 'string') {
		noteValue = todo.note;
	} else if (todo.note && typeof todo.note === 'object') {
		const note = todo.note as Record<string, unknown>;
		noteValue =
			typeof note.message === 'string'
				? note.message
				: typeof note.description === 'string'
					? note.description
					: undefined;
	}

	const candidates: (string | undefined)[] = [
		todo.description,
		todo.label,
		todo.name,
		todo.title,
		noteValue,
	];
	const match = candidates.find(
		(value): value is string => typeof value === 'string' && value.trim() !== '',
	);
	return match ?? 'N/A';
};

const getCategoryDisplay = (todo: TodoTableRow): string => {
	if (!todo.category) return 'Uncategorized';
	if (typeof todo.category === 'string') return todo.category;
	if (typeof todo.category === 'object') {
		const category = todo.category as Record<string, unknown>;
		return (
			(category.label as string) ||
			(category.name as string) ||
			(category.title as string) ||
			'Uncategorized'
		);
	}
	return 'Uncategorized';
};

const formatTodoDateWithTime = (todo: TodoTableRow): string => {
	if (todo.scheduledDateFormatted && todo.scheduledTimeFormatted) {
		return formatDateTime12Hour(todo.scheduledDateFormatted, todo.scheduledTimeFormatted);
	}
	return formatDateTime12Hour(todo.due_date ?? todo.dueDate, todo.dueTime);
};

const getTodoViewRoute = (todo: TodoTableRow): string =>
	ROUTES.TO_DO_ID.replace(':id', String(todo.id));

const EmployeeTodoWidget = ({ loading, items }: EmployeeTodoWidgetProps) => (
	<EmployeeWidgetCard
		title="My To-Dos"
		loading={loading}
		colSpan={6}
		emptyMessage="No records found"
		isEmpty={items.length === 0}
		header={
			<TableRow>
				<TableCell>Description</TableCell>
				<TableCell>Assigned</TableCell>
				<TableCell>Category</TableCell>
				<TableCell>Priority</TableCell>
				<TableCell>Status</TableCell>
				<TableCell>Due Date</TableCell>
			</TableRow>
		}
	>
		{items.map((todo) => {
			const viewRoute = getTodoViewRoute(todo);
			const description = getDescriptionDisplay(todo);

			return (
				<TableRow key={todo.id}>
					<TableCell>
						<NavLink to={viewRoute} className="employee-widget-link">
							{todo.isScheduleEvent ? (
								<Box display="flex" alignItems="center" gap={0.75}>
									<Repeat sx={{ fontSize: 14, color: 'text.secondary' }} />
									<span>{description}</span>
								</Box>
							) : (
								description
							)}
						</NavLink>
					</TableCell>
					<TableCell>{getAssignmentDisplay(todo)}</TableCell>
					<TableCell>{getCategoryDisplay(todo)}</TableCell>
					<TableCell>{getTodoPriorityDisplay(todo.priority)}</TableCell>
					<TableCell>{getTodoStatusDisplay(todo.status)}</TableCell>
					<TableCell>{formatTodoDateWithTime(todo)}</TableCell>
				</TableRow>
			);
		})}
	</EmployeeWidgetCard>
);

export default EmployeeTodoWidget;
